import _ from 'lodash'
import {
    SET_MESSAGES,
    ADD_TOAST,
    ADD_ERROR,
    ADD_WARNING,
    ADD_SUCCESS,
    ADD_INFO,
    ADD_MESSAGE,
    REMOVE_TOAST,
    REMOVE_TOASTS,
    RESHOW_NEWEST_MOTD,
    SEVERITY_ERROR,
    SEVERITY_WARNING,
    SEVERITY_SUCCESS,
    SEVERITY_INFO,
} from './constants'
import { toHashCode, createMessage } from './api'

// Initial state of the store
const initialState = {
    newestMessage: {
        id: -1,
        timeStamp: 0,
    },
    data: {},
}

// All notifications are built like this
const baseToast = {
    // A unique identifier
    id: '',

    // Imagine you have a notification with visibilityType.settings. It shows up when you have the
    // settings menu open. Then you close the settings menu and it disappears. Now you open the
    // settings menu again and it is still there, thus it is persistent. It only goes away when you
    // click on it to dismiss it
    persistent: false,

    // visibilityType: visibilityTypes.normal,
    // queue: ...
    queue: 'normal',

    isMessage: false,

    alwaysOnTop: false,

    // There are four supported severity types ['error', 'success', 'warning', 'info']
    // This setting determines look (css)
    severity: 'warning',

    // This controls where on the screen the notification should appear
    // We currently always use 'tr' (top-right)
    position: 'topRight',

    // Is the notification dismissible (can it be clicked away)?
    dismissible: true,

    // Should the notification disappear after x milliseconds?
    timeout: false,

    // How often have we recieved this notification
    count: 1,

    // How often have we recieved this notification since last dismissal
    countSinceLastDismissal: 1,

    // This is used by messages of the day so that we have access
    // to message specific data
    tag: null,

    // Message specific, so that we never dismiss the newest message, only hide it when
    // the user dismisses it. Then we show it again when the user chooses to open up the
    // notifications menu
    visible: true,

    dismissed: null,

    // Has this toast been seen (interesting for messages)
    // Could be used for toasts that are not dismissible as well...
    seen: false,
}

// Creates a localization object with the same content for all locales
function createSimpleLocalization(locales, title = '', content = '') {
    return locales.reduce((r, v) => {
        r[v] = { title, content }
        return r
    }, {})
}

// Create a regular toast from payload
function createToast(payload, severity, timeout = null) {
    const { locales, iid, created, updated, queue, ...options } = payload
    // Start by seting all values to default
    const result = {
        ...baseToast,
        iid,
        created,
        updated,
        queue,
    }

    // Set default timeout to 5 seconds for success toasts
    if (timeout == null) {
        if (severity === SEVERITY_SUCCESS) {
            result.timeout = 5000
        } else {
            result.timeout = false
        }
    }

    // If the options object is a single string, then all we need
    // to do is fake a localization for the notification
    if (typeof options === 'string') {
        result.title = ''
        result.content = options
        result.localized = createSimpleLocalization(locales, result.title, result.content)

        // Since we have an object, we go through the keys of the payload and
        // use them to modify our result
    } else {
        // Set queue if given
        if (options.queue != null && options.queue !== '') result.queue = options.queue

        // Set id if given
        if (options.id !== undefined) result.id = options.id

        // Set persistence
        if (options.persistent !== undefined) result.persistent = options.persistent

        // Set dismiss timeout
        result.timeout = options.timeout === undefined ? result.timeout : options.timeout

        // Set the always on top availabiliy
        if (options.alwaysOnTop !== undefined) result.alwaysOnTop = options.alwaysOnTop

        // If no localization is provided, then create it
        if (options.localized == null) {
            result.title = options.title != null ? options.title : ''
            result.content = options.content != null ? options.content : ''
            result.localized = createSimpleLocalization(locales, result.title, result.content)
            // Otherwise assume the given localization is in the correct format
        } else {
            try {
                result.title = options.localized.en.title
                result.content = options.localized.en.content
            } catch (error) {
                result.title = ''
                result.content = ''
            }

            result.localized = options.localized
        }
    }

    // If id is missing, create one from a hash of the message in english
    if (result.id == null || result.id === '') {
        result.id = toHashCode(result.content)
    }

    // Finally set the type of the notification
    result.severity = severity

    return result
}

// function createMessage(payload) {
//     const { locales, iid, created, updated, tag, ...options } = payload
//     // Start by seting all values to default
//     const result = {
//         ...baseToast,
//         iid,
//         created,
//         updated,
//         queue: 'normal',
//         isMessage: true,
//         tag,
//     }

//     // Set id if given
//     if (options.id !== undefined) result.id = options.id

//     // Set persistence
//     if (options.persistent !== undefined) result.persistent = options.persistent

//     // If id is missing, create one from a hash of the message in english
//     if (result.id == null || result.id === '') {
//         result.id = toHashCode(result.content)
//     }

//     // Finally set the type of the notification
//     result.severity = SEVERITY_INFO

//     return result
// }

function isSame(oldToast, newToast) {
    if (oldToast.id !== newToast.id) return false
    if (oldToast.queue !== newToast.queue) return false
    if (oldToast.isMessage !== newToast.isMessage) return false
    if (oldToast.alwaysOnTop !== newToast.alwaysOnTop) return false
    if (oldToast.severity !== newToast.severity) return false

    return true
}

function addNewToast(state, toast) {
    // First we add the toast to the data object in state
    state.data[toast.id] = toast

    // If this is a message, then we need to do some more stuff...
    if (toast.isMessage) {
        // Is this the newest motd we have seen?
        if (toast.tag.timeStamp > state.newestMessage.timeStamp) {
            // Update the newestMessage object to reflect this
            state.newestMessage = { id: toast.id, timeStamp: toast.tag.timeStamp }
        }
    }
}

// Add a given notification to the given state
function addToastToState(state, toast) {
    // This is the first time this toast is being added
    if (state.data[toast.id] == null || !isSame(state.data[toast.id], toast)) {
        addNewToast(state, toast)
        return
        // This is not the first time we see this id
    }

    // User has already dismissed this notification earlier
    if (state.data[toast.id].dismissed != null) {
        // How long ago since we last received this message
        if (toast.created - state.data[toast.id].dismissed > 59999) {
            addNewToast(state, toast)
            return
        }
    }

    // Either we are receiving a previous message again, or
    // Now we need to update all the updatable fields...
    state.data[toast.id].dismissible = toast.dismissible
    state.data[toast.id].timeout = toast.timeout
    state.data[toast.id].count += 1
    state.data[toast.id].countSinceLastDismissal += 1
    state.data[toast.id].updated = toast.updated
    state.data[toast.id].title = toast.title
    state.data[toast.id].content = toast.content
    state.data[toast.id].localized = toast.localized
    state.data[toast.id].dismissed = null
}

// Clones the state, adds the new notification and updates the list of notifications
function getNewStateWithToastOnAdded(state, toast) {
    // First we clone the current state
    const clone = _.cloneDeep(state)

    // Then we add the notification to the state
    addToastToState(clone, toast)

    // Finally we return an updated state
    return clone
}

// Clones the state and adds the new notifications before returning the cloned state
function getNewStateWithToastsAdded(state, toasts) {
    // First we clone the current state
    const clone = _.cloneDeep(state)

    // Then we add the notifications to the state
    // eslint-disable-next-line
    toasts.map(toast => {
        addToastToState(clone, toast)
    })

    // Finally we return an updated state
    return clone
}

function getSeverity(severity) {
    const severityTypes = [SEVERITY_ERROR, SEVERITY_INFO, SEVERITY_WARNING, SEVERITY_SUCCESS]

    if (severityTypes.indexOf(severity) < 0) {
        return SEVERITY_INFO
    }

    return severity
}

function addMessage(state, payload) {
    const base = { ...baseToast, ...payload.options }
    const toast = createMessage(base, payload.message)
    return getNewStateWithToastOnAdded(state, toast)
}

function addToast(state, payload, severity) {
    const severityType = getSeverity(severity)
    const toast = createToast(payload, severityType)
    return getNewStateWithToastOnAdded(state, toast)
}

// Commands for the state machine
const toaster = (state = initialState, action) => {
    const { payload, type } = action
    switch (type) {
        // Read given object into state
        case SET_MESSAGES: {
            const base = { ...baseToast, ...payload.options }
            const toasts = _.reduce(
                payload.messages,
                (result, message) => {
                    // First map into an toast
                    const motd = createMessage(base, message)

                    // Set seen status
                    motd.seen = message.seen

                    // Set visibility to the opposite of the seen status
                    motd.visible = !message.seen

                    // Finally add the toast to our list of toasts
                    result.push(motd)
                    return result
                },
                [],
            )
            const newState = getNewStateWithToastsAdded(state, toasts)
            return newState
        }
        // Add an error toast
        case ADD_ERROR: {
            return addToast(state, payload, SEVERITY_ERROR)
        }

        // Add a warning toast
        case ADD_WARNING: {
            return addToast(state, payload, SEVERITY_WARNING)
        }

        // Add a success toast
        case ADD_SUCCESS: {
            return addToast(state, payload, SEVERITY_SUCCESS)
        }

        // Add an info toast
        case ADD_INFO: {
            return addToast(state, payload, SEVERITY_INFO)
        }

        // Add a toast
        case ADD_TOAST: {
            return addToast(state, payload, payload.severity)
        }

        // Add a message
        case ADD_MESSAGE: {
            return addMessage(state, payload)
        }

        // Dismiss a toast
        case REMOVE_TOAST: {
            const toast = _.cloneDeep(state.data[payload.id])
            if (toast == null) return state

            // Mark the toast as seen and reset the count since last dismissed
            toast.seen = true
            toast.countSinceLastDismissal = 0

            if (!toast.dismissible) {
                return {
                    ...state,
                    data: {
                        ...state.data,
                        [toast.id]: toast,
                    },
                }
            }

            // If this is the newest message, we only want to hide it
            if (toast.isMessage && toast.id === state.newestMessage.id) {
                toast.visible = false
            } else {
                toast.dismissed = payload.dismissed
            }

            return {
                ...state,
                data: {
                    ...state.data,
                    [toast.id]: toast,
                },
            }
        }

        // Dismiss all given toasts
        case REMOVE_TOASTS: {
            if (payload.ids == null || payload.ids.length < 1) return state

            // Clone the entire data object
            const data = _.cloneDeep(state.data)
            if (_.isEmpty(data)) return state

            // Loop through the given ids, and update the state
            // accordingly
            payload.ids.map((id) => {
                const toast = data[id]
                if (toast == null) {
                    return 0
                }

                // Mark the toast as seen and reset the count since last dismissed
                toast.seen = true
                toast.countSinceLastDismissal = 0

                if (!toast.dismissible) {
                    return 0
                }

                // If this is the newest message, we only want to hide it
                if (toast.isMessage && toast.id === state.newestMessage.id) {
                    toast.visible = false
                } else {
                    toast.dismissed = payload.dismissed
                }

                return 0
            })

            return {
                ...state,
                data,
            }
        }

        // Reshow the newest motd
        case RESHOW_NEWEST_MOTD: {
            if (state.newestMessage.id < 0) return state

            if (state.data[state.newestMessage.id] == null) return state

            const motd = { ...state.data[state.newestMessage.id] }
            motd.visible = true

            return {
                ...state,
                data: {
                    ...state.data,
                    [motd.id]: motd,
                },
            }
        }

        // Default state
        default:
            return state
    }
}

export default toaster



// WEBPACK FOOTER //
// ./src/features/Toaster/data/toasts/reducers.js